/**
 * Base defines the properties each entry module has
 * by default
 */

import { getEntriesOfType } from '@/api/entries'
import {
  paginationGetters,
  paginationMutations,
  paginationState
} from '@/store/paginationHelpers'
import { queryGetters, queryMutations, queryState } from '@/store/queryHelpers'

export const createBaseState = () => ({
  ...paginationState,
  ...queryState,
  entries: null,
  loading: false,
  error: false
})

export const baseMutations = {
  ...paginationMutations,
  ...queryMutations,
  setLoading(state, payload) {
    state.loading = payload
  },
  setErrorLoadingEntries(state, payload) {
    state.error = payload
  },
  addEntries: (state, newEntries) => {
    if (!Array.isArray(state.entries) || !state.entries.length) {
      state.entries = [...newEntries]
      return
    }

    const entries = [...state.entries]

    // In case pagination is used, check that no duplicates is added
    if (Array.isArray(newEntries) && newEntries.length) {
      newEntries.forEach((newEntry) => {
        const newEntryId = newEntry.id
        const newEntryType = newEntry.type

        if (
          !entries.some(
            (currentEntry) =>
              currentEntry.id === newEntryId &&
              currentEntry.type === newEntryType
          )
        ) {
          entries.push(newEntry)
        }
      })
    }

    state.entries = entries
  },
  removeEntries: (state) => {
    state.entries = null
  },
  updateEntry: (state, { id, data }) => {
    if (!Array.isArray(state.entries)) {
      return
    }

    const idx = state.entries.findIndex((entry) => entry.id === id)

    if (idx === -1) {
      return
    }

    state.entries[idx] = {
      ...state.entries[idx],
      ...data
    }

    state.entries = [...state.entries]
  }
}

export const baseGetters = {
  ...paginationGetters,
  ...queryGetters,
  entries: (state) => {
    if (!Array.isArray(state.entries)) {
      return []
    }

    return state.entries
  },
  errorWhileLoadingEntries: (state) => {
    return state.error
  },
  hasLoadedEntries: (state) => {
    return !state.loading
  },
  /**
   * Gets an entry from all entries
   */
  getEntry: (state) => (id) => {
    return state.entries?.find((entry) => {
      return entry.id === id
    })
  }
}

export const createBaseActions = ({ entryType }) => {
  return {
    getEntries: async (
      { commit, getters },
      { page = undefined, reset = false } = {}
    ) => {
      const newPage = page ?? 1
      commit('setErrorLoadingEntries', false)
      commit('setLoading', true)

      let entriesOfType

      try {
        entriesOfType = await getEntriesOfType(
          entryType,
          getters.params,
          newPage
        )
      } catch {
        commit('removeEntries')
        commit('setPaginationData', null)
        commit('setLoading', false)
        commit('setErrorLoadingEntries', true)
        return []
      }

      const { data, meta } = entriesOfType

      let entries = data

      // Add flag for page
      entries = entries.map((entry) => ({ ...entry, page: newPage }))

      if (reset === true) {
        commit('removeEntries')
      }
      commit('addEntries', entries)
      commit('setPaginationData', meta)
      commit('setLoading', false)

      return entries
    },
    getNextPage: ({ dispatch, getters }) => {
      return dispatch('getEntries', { page: getters.currentPage + 1 })
    },
    getPage: ({ dispatch }, page) => {
      return dispatch('getEntries', { page })
    },
    toggleStatusToParams: ({ commit, getters }, status) => {
      if (!getters.params.status) {
        commit('setFilterParams', { status: [status] })
        return
      }

      if (getters.params.status.includes(status)) {
        const newStatuses = getters.params.status.filter(
          (existingStatus) => existingStatus !== status
        )

        commit('setFilterParams', {
          status: newStatuses
        })
      } else {
        commit('setFilterParams', {
          status: [...getters.params.status, status]
        })
        return
      }

      if (getters.params.status.length === 0) {
        commit('removeFilterParam', 'status')
      }
    },
    markAsPaid() {
      // MarkAsPaid intentionally left abstract
    }
  }
}
